import { useState, useRef, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@c2fo/liquidity';
import { GemLightIcon } from '@c2fo/liquidity/icons';
import PageBottomPortal from '@/enterprise/components/PageBottomPortal/PageBottomPortal';
import { useReporting } from '@/reporting';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import NavigationBar from '../NavigationBar';
import { useOfferBuilderContext } from '../OfferBuilderContext';
import OfferSummary from '../OfferSummary';
import { PreferredStepSkipReason } from '../usePreferredRateEligibility';
import { buildTakerMarketKey, getNameYourRateMarkets, setTakerMarketError, hasAnyTakerMarketError } from '../utils';
import PricingStrategyOptions from './PricingStrategyOptions';

const RateSelectLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dispatch, state } = useOfferBuilderContext();
  const { error, selected, workingTakerMarkets, disableNavigation, submissionComplete, preferredRateSelected } = state;
  const { trackEnterpriseEvent } = useReporting();
  // Resolved once PricingStrategyOptionsComponent fetches pricing inside its own suspense boundary
  const [showPreferredStep, setShowPreferredStep] = useState(false);
  const skipReasonRef = useRef<PreferredStepSkipReason | null>(null);
  const handlePreferredStepChange = useCallback(
    ({ show, skipReason }: { show: boolean; skipReason: PreferredStepSkipReason | null }) => {
      setShowPreferredStep(show);
      skipReasonRef.current = skipReason;
    },
    []
  );

  if (selected.length === 0 || submissionComplete) {
    return <Navigate to={asEnterpriseLink('build-offer')} replace />;
  }

  const hasError = hasAnyTakerMarketError(state, 'NO_RATE_SELECTED');

  const onBackClick = () => {
    if (error) {
      dispatch({ type: 'CLEAR_ERROR' });
    }

    navigate(asEnterpriseLink('build-offer/frequency'));
  };

  const onNextClick = () => {
    let hasInvalidMarkets = false;
    const nameYourRateMarkets = getNameYourRateMarkets(selected);

    nameYourRateMarkets.forEach((takerMarket) => {
      const marketKey = buildTakerMarketKey(takerMarket);
      const workingMarket = workingTakerMarkets[marketKey];

      // check if this taker market has a rate set
      if (!workingMarket?.rate) {
        const setError = setTakerMarketError(dispatch);
        setError(marketKey, 'NO_RATE_SELECTED');
        hasInvalidMarkets = true;
      }
    });

    if (hasInvalidMarkets) {
      return;
    }

    if (!showPreferredStep && skipReasonRef.current) {
      trackEnterpriseEvent('preferred-rate-upgrade::skipped', {
        reason: skipReasonRef.current,
      });
    }

    const destination = showPreferredStep ? 'build-offer/preferred-upgrade' : 'build-offer/review';
    navigate(asEnterpriseLink(destination));
  };

  const handleUndoUpgrade = () => {
    trackEnterpriseEvent('preferred-rate-upgrade::deselected', {
      source: 'rate-select',
    });
    dispatch({ type: 'CLEAR_PREFERRED_RATE_STATE' });
  };

  return (
    <>
      {preferredRateSelected && (
        <div className="mb-6 flex flex-col rounded-xl bg-purple-100 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
            <GemLightIcon className="size-10 shrink-0 fill-purple-700" />
            <div>
              <div className="font-medium">{t('builder.preferredOfferUpgrade')}</div>
              <div className="text-sm">{t('builder.preferredOfferUpgradeDescription')}</div>
            </div>
          </div>
          <Button className="mt-2 w-fit sm:mt-0" onClick={handleUndoUpgrade} size="sm" variant="link">
            {t('builder.undoUpgrade')}
          </Button>
        </div>
      )}
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="lg:w-7/12">
          <div
            aria-disabled={preferredRateSelected || undefined}
            className={preferredRateSelected ? 'pointer-events-none opacity-38' : ''}
            tabIndex={preferredRateSelected ? -1 : undefined}
          >
            <PricingStrategyOptions onShowPreferredStep={handlePreferredStepChange} />
          </div>
        </div>
        <div className="flex-1">
          <div className="lg:sticky lg:top-24">
            <div className="mb-2 text-lg font-medium">{t('builder.offerSummary')}</div>
            {hasError && (
              <div className="mb-2">
                <Alert description={t('builder.rateError')} icon={false} full type="error" variant="outlined" />
              </div>
            )}
            <div className="rounded-lg bg-deepBlue-50 p-2">
              <OfferSummary offerSubmitting={false} step="rate-select" />
            </div>
          </div>
        </div>
      </div>
      {!disableNavigation && (
        <PageBottomPortal>
          <NavigationBar>
            <div className="flex w-full shrink-0 flex-col items-center gap-4 md:ml-auto md:w-fit md:flex-row">
              <Button className="w-full md:w-fit" variant="secondary" onClick={onBackClick}>
                {t('core.back')}
              </Button>
              <Button className="w-full md:w-fit" onClick={onNextClick}>
                {t('core.next')}
              </Button>
            </div>
          </NavigationBar>
        </PageBottomPortal>
      )}
    </>
  );
};

export default RateSelectLayout;
